import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { fetchInstructors } from "../../store/instructorsSlice";
import { fetchUsers } from "../../store/usersSlice";
import { UserContext } from "../../UserContext";
import {
   getInstructorTypeLabel,
   getStaffTitle,
   INSTRUCTOR_TYPE_OPTIONS,
   normalizeInstructorExam,
   normalizeInstructorType,
} from "../../constants/instructors";
import { openPopup as openPopupUI } from "../Common/popupUIStore";
import StudentItem from "../Common/StudentItem";
import { DropdownUI, SearchBarUI } from "../UI";
import useAutoLoadMore from "../../hooks/useAutoLoadMore";
import addIcon from "../../assets/svg/add.svg";

const PAGE_SIZE = 24;
const PAGE_SIZE_MOBILE = 8;
const MOBILE_BP = 768;

const FILTER_OPTIONS = [
   { value: "ALL", label: "Toți" },
   { value: "INSTRUCTOR", label: "Instructori" },
   ...INSTRUCTOR_TYPE_OPTIONS.map((option) => ({
      value: `INSTRUCTOR_TYPE:${option.value}`,
      label: `Instr. ${option.label}`,
   })),
   { value: "ADMIN", label: "Admini" },
   { value: "MANAGER", label: "Manageri" },
   { value: "PROFESSOR", label: "Profesori" },
];

const ROLE_LABELS = {
   INSTRUCTOR: "Instructor",
   MANAGER: "Manager",
   ADMIN: "Admin",
   PROFESSOR: "Profesor",
};

const ROLE_ORDER = {
   INSTRUCTOR: 0,
   ADMIN: 1,
   MANAGER: 2,
   PROFESSOR: 3,
};

const CREATE_BUTTONS = ["INSTRUCTOR", "ADMIN", "MANAGER", "PROFESSOR"];
const CREATE_DROPDOWN_OPTIONS = [
   { value: "INSTRUCTOR", label: "Instructor" },
   { value: "ADMIN", label: "Admin" },
   { value: "MANAGER", label: "Manager" },
   { value: "PROFESSOR", label: "Profesor" },
];


function isUnauthorizedErrorMessage(value) {
   const message = String(value || "").toLowerCase();
   if (!message) return false;

   return (
      message.includes("unauthorized") ||
      message.includes("statuscode\":401") ||
      message.includes("status 401") ||
      message.includes("http 401")
   );
}

function useIsMobile(bp = MOBILE_BP) {
   const [isMobile, setIsMobile] = useState(() => {
      if (typeof window === "undefined") return false;
      return window.matchMedia(`(max-width:${bp}px)`).matches;
   });

   useEffect(() => {
      if (typeof window === "undefined") return undefined;

      const mq = window.matchMedia(`(max-width:${bp}px)`);
      const onChange = (event) => setIsMobile(event.matches);

      if (mq.addEventListener) mq.addEventListener("change", onChange);
      else mq.addListener(onChange);

      return () => {
         if (mq.removeEventListener) mq.removeEventListener("change", onChange);
         else mq.removeListener(onChange);
      };
   }, [bp]);

   return isMobile;
}

// un om intră în categoria aleasă din meniu (Toți, Instructori, un tip, un rol)
function matchesFilter(entry, filter) {
   if (filter === "ALL" || entry.kind === filter) return true;
   return (
      String(filter).startsWith("INSTRUCTOR_TYPE:") &&
      entry.kind === "INSTRUCTOR" &&
      normalizeInstructorType(entry.instructorType) === String(filter).split(":")[1]
   );
}

function normalizeRole(value) {
   return String(value || "")
      .trim()
      .toUpperCase();
}




function normalizeColorToCss(value, fallback) {
   const raw = String(value || "").trim();

   if (!raw) return fallback;
   if (raw.startsWith("var(")) return raw;
   if (raw.startsWith("--")) {
      const short = raw.slice(2).trim().toLowerCase();
      if (!short || short === "default") return "var(--event-default)";
      if (short.startsWith("event-")) return `var(--${short})`;
      return `var(--event-${short})`;
   }
   if (/^event-/i.test(raw)) return `var(--${raw.toLowerCase()})`;
   return raw;
}

export default function PersonalManager() {
   const dispatch = useDispatch();
   const { user } = useContext(UserContext);
   const {
      list: instructors = [],
      status: instructorsStatus = "idle",
      error: instructorsError = null,
   } = useSelector((state) => state.instructors || {});
   const {
      list: users = [],
      status: usersStatus = "idle",
      error: usersError = null,
   } = useSelector((state) => state.users || {});

   const isMobile = useIsMobile(MOBILE_BP);
   const isAdmin = normalizeRole(user?.role) === "ADMIN";
   const pageSize = isMobile ? PAGE_SIZE_MOBILE : PAGE_SIZE;

   const [search, setSearch] = useState("");
   const [filter, setFilter] = useState("ALL");
   const [visibleCount, setVisibleCount] = useState(pageSize);

   // listele sunt păstrate în browser (redux-persist): se arată imediat, iar la fiecare deschidere a
   // paginii se reîmprospătează o dată în fundal (poze, oameni noi, schimbări de pe alt calculator)
   useEffect(() => {
      dispatch(fetchInstructors());
      dispatch(fetchUsers());
   }, [dispatch]);

   useEffect(() => {
      if (instructorsStatus === "failed") dispatch(fetchInstructors());
      if (usersStatus === "failed") dispatch(fetchUsers());
   }, [dispatch, instructorsStatus, usersStatus]);

   const usersById = useMemo(() => {
      const map = new Map();

      for (const user of users || []) {
         const key = String(user?.id ?? "").trim();
         if (!key) continue;
         map.set(key, user);
      }

      return map;
   }, [users]);

   const personnel = useMemo(() => {
      const items = [];

      for (const instructor of Array.isArray(instructors) ? instructors : []) {
         const linkedUser = usersById.get(String(instructor?.userId ?? ""));
         const firstName = instructor?.firstName || "";
         const lastName = instructor?.lastName || "";
         const email = String(
            linkedUser?.email || instructor?.email || "",
         ).trim();
         const phone = String(instructor?.phone || "").trim();
         const sector = String(instructor?.sector || "").trim();
         const instructorType = normalizeInstructorType(
            instructor?.instructorType,
         );
         const instructorTypeLabel = getInstructorTypeLabel(instructorType);
         const isExamen = normalizeInstructorExam(instructor?.isExamen);
         const fullName = `${firstName} ${lastName}`.trim();
         const rawColor = String(
            instructor?.color || linkedUser?.color || "",
         ).trim();

         items.push({
            id: `instructor-${instructor?.id ?? fullName}`,
            kind: "INSTRUCTOR",
            roleLabel: ROLE_LABELS.INSTRUCTOR,
            firstName,
            lastName,
            fullName,
            phone,
            email,
            color: rawColor,
            instructorType,
            isExamen,
            displayColor: rawColor ? normalizeColorToCss(rawColor) : undefined,
            // doar titlul („Instructor de examen”): înainte tipul „Examen” + „poate examina”
            // dădeau „Examen • Examen”, iar telefonul / sectorul încărcau caseta
            secondaryText: getStaffTitle("INSTRUCTOR", instructorType),
            searchText:
               `${fullName} ${phone} ${email} ${sector} ${rawColor} ${instructorType} ${instructorTypeLabel} ${isExamen ? "examen" : ""}`.toLowerCase(),
            record: instructor,
         });
      }

      for (const user of Array.isArray(users) ? users : []) {
         const role = normalizeRole(user?.role);

         if (
            role !== "MANAGER" &&
            role !== "ADMIN" &&
            role !== "PROFESSOR"
         ) {
            continue;
         }

         const firstName = user?.firstName || "";
         const lastName = user?.lastName || "";
         const fullName = `${firstName} ${lastName}`.trim();
         const phone = String(user?.phone || "").trim();
         const email = String(user?.email || "").trim();
         const supportsColor = role === "MANAGER" || role === "ADMIN";
         const rawColor = supportsColor ? String(user?.color || "").trim() : "";

         items.push({
            id: `${role.toLowerCase()}-${user?.id ?? fullName}`,
            kind: role,
            roleLabel: ROLE_LABELS[role] || role,
            firstName,
            lastName,
            fullName,
            phone,
            email,
            color: rawColor,
            displayColor: rawColor ? normalizeColorToCss(rawColor) : undefined,
            secondaryText: getStaffTitle(role),
            searchText:
               `${fullName} ${phone} ${email} ${role} ${rawColor}`.toLowerCase(),
            record: user,
         });
      }

      return items.sort((left, right) => {
         const roleDiff =
            (ROLE_ORDER[left.kind] ?? 99) - (ROLE_ORDER[right.kind] ?? 99);

         if (roleDiff !== 0) return roleDiff;

         return left.fullName.localeCompare(right.fullName, "ro", {
            sensitivity: "base",
         });
      });
   }, [instructors, users, usersById]);

   const normalizedQuery = useMemo(
      () =>
         String(search || "")
            .trim()
            .toLowerCase(),
      [search],
   );

   // întâi căutarea (din ea ies și numerele din meniu), apoi categoria
   const searchedPersonnel = useMemo(
      () =>
         normalizedQuery
            ? personnel.filter((entry) => entry.searchText.includes(normalizedQuery))
            : personnel,
      [normalizedQuery, personnel],
   );

   const filteredPersonnel = useMemo(
      () => searchedPersonnel.filter((entry) => matchesFilter(entry, filter)),
      [filter, searchedPersonnel],
   );

   useEffect(() => {
      setVisibleCount(pageSize);
   }, [filter, normalizedQuery, pageSize]);

   const visiblePersonnel = useMemo(
      () => filteredPersonnel.slice(0, visibleCount),
      [filteredPersonnel, visibleCount],
   );

   const totalResults = filteredPersonnel.length;
   const canLoadMore = visibleCount < totalResults;
   const usersUnauthorized = isUnauthorizedErrorMessage(usersError);
   const hasData = personnel.length > 0;
   const hasError = hasData
      ? null
      : instructorsError || (usersUnauthorized ? null : usersError);
   const isLoading =
      instructorsStatus === "loading" || usersStatus === "loading";
   const filterOptions = useMemo(
      () =>
         usersUnauthorized
            ? FILTER_OPTIONS.filter(
                 (option) =>
                    option.value === "ALL" ||
                    option.value === "INSTRUCTOR" ||
                    String(option.value).startsWith("INSTRUCTOR_TYPE:"),
              )
            : FILTER_OPTIONS,
      [usersUnauthorized],
   );
   // meniul de sus: fiecare categorie cu câți oameni are (după căutare)
   const filterMenu = useMemo(
      () =>
         filterOptions.map((option) => ({
            ...option,
            label: `${option.label} · ${
               searchedPersonnel.filter((entry) => matchesFilter(entry, option.value)).length
            }`,
         })),
      [filterOptions, searchedPersonnel],
   );
   const createOptions = useMemo(
      () =>
         CREATE_DROPDOWN_OPTIONS.filter(
            (option) => option.value !== "ADMIN" || isAdmin,
         ),
      [isAdmin],
   );

   useEffect(() => {
      if (!filterOptions.some((option) => option.value === filter)) {
         setFilter("ALL");
      }
   }, [filter, filterOptions]);

   // fără „Afișează încă”: următorii vin singuri aproape de capătul listei, ca pe Acasă
   const autoLoad = useAutoLoadMore(!isLoading && !hasError && canLoadMore, () =>
      setVisibleCount((current) => Math.max(current, Math.min(visibleCount + pageSize, totalResults))),
   );

   const handleOpenCreate = (role = null) => {
      if (role === "ADMIN" && !isAdmin) return;

      openPopupUI("personalCreate", {
         initialRole: role,
      });
   };

   const handleCreateSelect = (value) => {
      if (value === "ADMIN" && !isAdmin) {
         return;
      }

      if (CREATE_BUTTONS.includes(value)) {
         handleOpenCreate(value);
      }
   };

   const handleOpenEntry = (entry) => {
      if (entry.kind === "INSTRUCTOR") {
         openPopupUI("instructorDetails", { instructor: entry.record });
         return;
      }

      if (entry.kind === "MANAGER" || entry.kind === "ADMIN") {
         openPopupUI("managerAdminDetails", { user: entry.record });
         return;
      }

      if (entry.kind === "PROFESSOR") {
         openPopupUI("professorDetails", { user: entry.record });
      }
   };

   return (
      <div className="personalPage">
         {/* rândul de sus, ca pe Acasă (scss/_pageToolbar.scss) */}
         <div className="pageToolbar">
            <DropdownUI
               size="lg"
               surface="page"
               value={filter}
               onChange={setFilter}
               options={filterMenu}
               aria-label="Categorie"
               className="pageToolbar__menu"
            />

            <SearchBarUI
               surface="page"
               value={search}
               onChange={(event) => setSearch(event.target.value)}
               onClear={() => setSearch("")}
               placeholder="Caută după nume / telefon / email…"
               aria-label="Caută personal"
               className="pageToolbar__search"
            />

            <div className="pageToolbar__controls">
               <DropdownUI
                  tone="primary"
                  size="lg"
                  align="end"
                  icon={addIcon}
                  label={isMobile ? "" : "Adaugă"}
                  options={createOptions}
                  onChange={handleCreateSelect}
                  aria-label="Adaugă personal"
               />
            </div>
         </div>

         <section className="personalPageUI">
            <div className="personalPageUI__header">
               <div className="personalPageUI__titleWrap">
                  <h2 className="personalPageUI__title">Personal</h2>
               </div>
            </div>

            <div className="personalPageUI__gridWrap">
               <div className="personalPageUI__grid" ref={autoLoad.ref} onScroll={autoLoad.onScroll}>
                  {isLoading && (
                     <p className="personalPageUI__status">
                        Se încarcă lista de personal...
                     </p>
                  )}

                  {!isLoading && hasError && (
                     <p className="personalPageUI__status personalPageUI__status--error">
                        {hasError}
                     </p>
                  )}

                  {!isLoading &&
                     !hasError &&
                     visiblePersonnel.map((entry) => (
                        <StudentItem
                           key={entry.id}
                           student={entry}
                           avatarPerson={entry.record}
                           color={entry.displayColor}
                           onOpen={() => handleOpenEntry(entry)}
                           highlightQuery={search}
                           secondaryText={entry.secondaryText}
                           className={`personalPageUI__item personalPageUI__item--${entry.kind.toLowerCase()}`}
                        />
                     ))}

                  {!isLoading && !hasError && totalResults === 0 && (
                     <p className="personalPageUI__empty">
                        Nu există rezultate pentru filtrul curent.
                     </p>
                  )}
               </div>
            </div>
         </section>
      </div>
   );
}
